/* TeacherHubBrowser — teacher-facing DeskeyHUB

   Lets the teacher:
   - Browse every department's library (read-only content + download)
   - Submit a new document to any department (awaiting admin review)
   - Track status of their own submissions across all departments
*/
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import {
  Library, FolderOpen, FileText, Upload, Search, X, ArrowLeft, ArrowRight,
  Clock, CheckCircle, XCircle, Eye, ChevronRight, AlertCircle, Send,
  BookOpen, Plus, Zap
} from './DeskeyIcons';
import { Button } from './ui/button';
import DocViewer from './DocViewer';
import { friendlyError } from '../lib/errors';

const API = process.env.REACT_APP_BACKEND_URL;
const jsonApi = async (p, opts = {}) => {
  const t = localStorage.getItem('desky_token');
  const r = await fetch(`${API}/api${p}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}), ...opts.headers },
  });
  let txt = '';
  try { txt = await r.text(); } catch { /* swallow */ }
  let d = null;
  if (txt) { try { d = JSON.parse(txt); } catch { /* not json */ } }
  if (!r.ok) {
    const fallback = r.status === 400 ? 'Bad request'
      : r.status === 401 ? 'Session expired — please log in again'
      : r.status === 403 ? 'You don\'t have permission for this action'
      : r.status === 404 ? 'Not found'
      : r.status === 409 ? 'Conflict — that name or code is already in use'
      : r.status === 413 ? 'File is too large'
      : r.status === 415 ? 'Unsupported file type'
      : `Request failed (${r.status})`;
    const detail = (d && (d.detail || d.message)) || fallback;
    return { detail, _status: r.status, ...(d || {}) };
  }
  return d || {};
};
const formApi = async (p, fd) => {
  const t = localStorage.getItem('desky_token');
  let r;
  try {
    r = await fetch(`${API}/api${p}`, { method: 'POST', headers: { ...(t ? { Authorization: `Bearer ${t}` } : {}) }, body: fd });
  } catch {
    return { detail: 'Network error — the file was not uploaded', _status: 0 };
  }
  let d = null;
  try { d = await r.json(); } catch { /* non-JSON error body (proxy 413, HTML 502…) */ }
  // Callers treat a missing `detail` as success. Without this an upload that
  // failed with a non-JSON body closed the modal as if it had worked, and the
  // document then never showed up under My Submissions.
  if (!r.ok) {
    return {
      ...(d || {}),
      detail: d?.detail || (r.status === 413 ? 'File is too large' : `Upload failed (HTTP ${r.status})`),
      _status: r.status,
    };
  }
  return d || {};
};
const fmtBytes = (b) => { if (!b) return '0 B'; const u = ['B','KB','MB','GB']; let i=0,n=b; while(n>=1024&&i<u.length-1){n/=1024;i++;} return `${n.toFixed(n>=10||i===0?0:1)} ${u[i]}`; };
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '--';

const fileBadge = (mime, name) => {
  const ext = (name || '').split('.').pop()?.toLowerCase();
  if ((mime || '').includes('pdf') || ext === 'pdf') return { bg: '#ef4444', label: 'PDF' };
  if (['doc', 'docx'].includes(ext)) return { bg: '#2563eb', label: 'DOC' };
  if (['xls', 'xlsx'].includes(ext)) return { bg: '#059669', label: 'XLS' };
  if (['ppt', 'pptx'].includes(ext)) return { bg: '#ea580c', label: 'PPT' };
  if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return { bg: '#8b5cf6', label: 'IMG' };
  return { bg: '#64748b', label: ext?.toUpperCase() || 'FILE' };
};

export default function TeacherHubBrowser() {
  const A = '#34A853';
  const [tab, setTab] = useState('browse'); // browse | my-submissions
  const [depts, setDepts] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mySubs, setMySubs] = useState([]);

  const loadDepts = useCallback(async () => {
    setLoading(true);
    const r = await jsonApi('/hub/departments');
    setDepts(r?.departments || []);
    setLoading(false);
  }, []);

  const loadMy = useCallback(async () => {
    const r = await jsonApi('/hub/submissions/mine/all');
    setMySubs(r?.submissions || []);
  }, []);

  useEffect(() => { loadDepts(); loadMy(); }, [loadDepts, loadMy]);

  const pendingMine = mySubs.filter(s => s.status === 'pending').length;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="teacher-hub">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
            <Library className="w-5 h-5" style={{ color: A }} /> DeskeyHUB
          </h2>
          <p className="text-[12px] text-gray-500 mt-0.5">Browse every department's library, download research, and contribute your own PDFs & docs.</p>
        </div>
      </div>
      <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5 w-fit">
        {[
          { id: 'browse', label: 'Browse Libraries', icon: FolderOpen },
          { id: 'my-submissions', label: 'My Submissions', icon: Send, badge: pendingMine },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all ${tab === t.id ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'}`} data-testid={`teacher-hub-tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
            {!!t.badge && <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full tabular-nums">{t.badge}</span>}
          </button>
        ))}
      </div>

      {tab === 'browse' && !selectedDept && (
        loading ? (
          <div className="flex items-center justify-center h-48"><div className="w-7 h-7 border-2 border-gray-200 border-t-slate-500 rounded-full animate-spin" /></div>
        ) : depts.length === 0 ? (
          <p className="text-[12px] text-gray-400 text-center py-10">No libraries accessible.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="teacher-hub-depts">
            {depts.map(d => (
              <button key={d.id} onClick={() => setSelectedDept(d)} className="bg-white border border-gray-200 rounded-xl p-4 text-left hover:shadow-md hover:-translate-y-0.5 hover:border-[#34A853]/40 transition-all group" data-testid={`teacher-hub-dept-${d.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: `${A}15` }}>
                    <Library className="w-5 h-5" style={{ color: A }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-[14px] font-extrabold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{d.name}</h3>
                      <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-[#34A853] transition-transform group-hover:translate-x-0.5 flex-shrink-0" />
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5">{d.document_count || 0} documents · {d.folder_count || 0} folders</p>
                    {d.teacher_contributions > 0 && <p className="text-[10px] text-purple-600 font-semibold mt-1">{d.teacher_contributions} teacher contribution{d.teacher_contributions === 1 ? '' : 's'}</p>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )
      )}

      {tab === 'browse' && selectedDept && (
        <DeptLibraryView A={A} dept={selectedDept} mySubs={mySubs} onBack={() => setSelectedDept(null)} onSubmitted={loadMy} />
      )}

      {tab === 'my-submissions' && (
        <MySubmissionsView A={A} subs={mySubs} onRefresh={loadMy} />
      )}
    </div>
  );
}

function DeptLibraryView({ A, dept, mySubs = [], onBack, onSubmitted }) {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [folders, setFolders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [search, setSearch] = useState('');
  const [subjectSearch, setSubjectSearch] = useState('');
  const [showSubmit, setShowSubmit] = useState(false);
  const [showNewSubject, setShowNewSubject] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [autoStatus, setAutoStatus] = useState({ enabled: false });
  const [loading, setLoading] = useState(true);

  const loadSubjects = useCallback(async () => {
    const r = await jsonApi(`/hub/repo/${dept.id}/subjects`);
    setSubjects(r?.subjects || []);
  }, [dept.id]);

  const loadAuto = useCallback(async () => {
    const r = await jsonApi(`/hub/repo/${dept.id}/auto-approval/status`);
    setAutoStatus({ enabled: !!r?.enabled, enabled_by_name: r?.enabled_by_name });
  }, [dept.id]);

  const loadInner = useCallback(async () => {
    if (!selectedSubject) { setFolders([]); setDocs([]); setBreadcrumbs([]); setTotalDocs(0); return; }
    const fParams = new URLSearchParams();
    fParams.set('subject_id', selectedSubject.id);
    if (currentFolder) fParams.set('parent_id', currentFolder.id);
    const dParams = new URLSearchParams();
    dParams.set('subject_id', selectedSubject.id);
    if (currentFolder) dParams.set('folder_id', currentFolder.id);
    if (search.trim()) { dParams.set('q', search.trim()); dParams.set('folder_id', '*'); }
    const tParams = new URLSearchParams();
    tParams.set('subject_id', selectedSubject.id);
    tParams.set('folder_id', '*');
    const [f, d, t] = await Promise.all([
      jsonApi(`/hub/repo/${dept.id}/folders?${fParams}`),
      jsonApi(`/hub/repo/${dept.id}/documents?${dParams}`),
      jsonApi(`/hub/repo/${dept.id}/documents?${tParams}`),
    ]);
    setFolders(f?.folders || []);
    setBreadcrumbs(f?.breadcrumbs || []);
    setDocs(d?.documents || []);
    setTotalDocs((t?.documents || []).length);
  }, [dept.id, currentFolder, search, selectedSubject]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadSubjects(), loadAuto()]).finally(() => setLoading(false));
  }, [loadSubjects, loadAuto]);

  useEffect(() => { loadInner(); }, [loadInner]);

  const filteredSubjects = useMemo(() => {
    if (!subjectSearch.trim()) return subjects;
    const q = subjectSearch.toLowerCase();
    return subjects.filter(s => (s.name || '').toLowerCase().includes(q) || (s.code || '').toLowerCase().includes(q));
  }, [subjects, subjectSearch]);

  // Uploads that are still awaiting admin review don't appear in `docs` yet
  // (they only publish to hub_documents on approval) — surface a hint here
  // so "my file disappeared" reads as "it's pending review", not "it failed".
  const myPendingHere = useMemo(() => {
    if (!selectedSubject) return 0;
    return mySubs.filter(s => s.status === 'pending' && s.target_department_id === dept.id && s.target_subject_id === selectedSubject.id).length;
  }, [mySubs, dept.id, selectedSubject]);

  return (
    <div className="space-y-3" data-testid="teacher-hub-dept-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors" data-testid="teacher-hub-back"><ArrowLeft className="w-4 h-4 text-gray-600" /></button>
          <div>
            <h3 className="text-[15px] font-extrabold text-gray-900 flex items-center gap-2 flex-wrap" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Library className="w-4 h-4" style={{ color: A }} /> {dept.name}
              {autoStatus.enabled && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-100">
                  <Zap className="w-2.5 h-2.5" /> Auto-publish ON
                </span>
              )}
            </h3>
            <p className="text-[11px] text-gray-500">{subjects.length} subject{subjects.length === 1 ? '' : 's'}{selectedSubject ? ` · ${totalDocs} doc${totalDocs === 1 ? '' : 's'} in ${selectedSubject.name}` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!selectedSubject && (
            <>
              <Button size="sm" variant="outline" onClick={() => setShowNewSubject(true)} className="gap-1.5 h-8" data-testid="teacher-hub-new-subject">
                <Plus className="w-3.5 h-3.5" /> Suggest Subject
              </Button>
              <Button size="sm" onClick={() => setShowSubmit(true)} style={{ background: A }} className="text-white gap-1.5 h-8" data-testid="teacher-hub-submit-dept-btn">
                <Send className="w-3.5 h-3.5" /> {autoStatus.enabled ? 'Publish to this Library' : 'Submit Document'}
              </Button>
            </>
          )}
          {selectedSubject && (
            <>
              <Button size="sm" variant="outline" onClick={() => setShowNewFolder(true)} className="gap-1.5 h-8" data-testid="teacher-hub-new-folder">
                <Plus className="w-3.5 h-3.5" /> New Folder
              </Button>
              <Button size="sm" onClick={() => setShowSubmit(true)} style={{ background: A }} className="text-white gap-1.5 h-8" data-testid="teacher-hub-submit-btn">
                <Send className="w-3.5 h-3.5" /> {autoStatus.enabled ? 'Publish to this Subject' : 'Contribute to this Subject'}
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10"><div className="w-6 h-6 border-2 border-gray-200 border-t-slate-500 rounded-full animate-spin" /></div>
      ) : !selectedSubject ? (
        /* SUBJECTS LEVEL */
        <div className="space-y-3" data-testid="teacher-hub-subjects-level">
          <div className="relative max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input value={subjectSearch} onChange={e => setSubjectSearch(e.target.value)} placeholder="Search subjects..." className="w-full h-8 pl-8 pr-3 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white" data-testid="teacher-hub-subject-search" />
          </div>
          {filteredSubjects.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-gray-200 rounded-xl">
              <BookOpen className="w-12 h-12 text-gray-200 mx-auto mb-2" />
              <p className="text-[13px] text-gray-500 font-medium">{subjectSearch ? 'No subjects match' : 'No subjects yet'}</p>
              <p className="text-[11px] text-gray-400 mt-1">{subjectSearch ? '' : 'Be the first to start one — your subject will appear instantly inside this department.'}</p>
              {!subjectSearch && (
                <Button size="sm" onClick={() => setShowNewSubject(true)} className="mt-3 gap-1.5" style={{ background: A }} data-testid="teacher-hub-empty-new-subject"><Plus className="w-3.5 h-3.5 text-white" /><span className="text-white">Create the first subject</span></Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
              {filteredSubjects.map(s => {
                const accent = s.color || A;
                return (
                  <button key={s.id} onClick={() => setSelectedSubject(s)} className="bg-white border border-gray-200 rounded-xl p-3.5 text-left hover:shadow-md hover:-translate-y-0.5 hover:border-[#34A853]/40 transition-all group" data-testid={`teacher-hub-subject-${s.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: `${accent}15` }}>
                        <BookOpen className="w-5 h-5" style={{ color: accent }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-[14px] font-extrabold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.name}</h4>
                          {s.code && <span className="text-[10px] font-bold tabular-nums bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{s.code}</span>}
                          <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-[#34A853] ml-auto transition-transform group-hover:translate-x-0.5 flex-shrink-0" />
                        </div>
                        {s.description && <p className="text-[11px] text-gray-600 mt-0.5 line-clamp-2 leading-relaxed">{s.description}</p>}
                        <p className="text-[10px] text-gray-500 mt-1.5">{s.document_count || 0} docs · {s.folder_count || 0} folders</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* INSIDE A SUBJECT — folders + docs (read-only browsing) */
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-[11px] flex-wrap">
              <button onClick={() => { setSelectedSubject(null); setCurrentFolder(null); setSearch(''); }} className="flex items-center gap-1 px-2 py-1 rounded-md text-gray-600 hover:bg-gray-100 transition-colors" data-testid="teacher-hub-crumb-subjects"><BookOpen className="w-3 h-3" /> Subjects</button>
              <ChevronRight className="w-3 h-3 text-gray-300" />
              <button onClick={() => setCurrentFolder(null)} className={`flex items-center gap-1 px-2 py-1 rounded-md transition-colors ${!currentFolder ? 'font-bold text-gray-900 bg-gray-100' : 'text-gray-600 hover:bg-gray-100'}`}>
                {selectedSubject.name}{selectedSubject.code ? ` (${selectedSubject.code})` : ''}
              </button>
              {breadcrumbs.map((c, i) => (
                <React.Fragment key={c.id}>
                  <ChevronRight className="w-3 h-3 text-gray-300" />
                  <button onClick={() => setCurrentFolder(c)} className={`px-2 py-1 rounded-md ${i === breadcrumbs.length - 1 ? 'font-bold text-gray-900 bg-gray-100' : 'text-gray-600 hover:bg-gray-100'}`}>{c.name}</button>
                </React.Fragment>
              ))}
            </div>
            <div className="relative ml-auto max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="w-full h-8 pl-8 pr-3 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white" data-testid="teacher-hub-search" />
            </div>
          </div>

          {!search.trim() && folders.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Folders</p>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                {folders.map(f => (
                  <button key={f.id} onClick={() => setCurrentFolder({ id: f.id, name: f.name })} className="bg-white border border-gray-200 rounded-xl p-3 hover:shadow-sm hover:border-[#34A853]/40 transition-all flex items-center gap-2.5" data-testid={`teacher-hub-folder-${f.id}`}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${A}15` }}><FolderOpen className="w-4 h-4" style={{ color: A }} /></div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-[13px] font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{f.name}</p>
                      <p className="text-[10px] text-gray-500">{f.document_count || 0} docs</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {myPendingHere > 0 && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <Clock className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
              <p className="text-[11px] text-amber-800">You have {myPendingHere} submission{myPendingHere === 1 ? '' : 's'} awaiting admin review in this subject — track status in <strong>My Submissions</strong>.</p>
            </div>
          )}

          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Documents {search.trim() && `— "${search}"`}</p>
            {docs.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-gray-200 rounded-xl">
                <FileText className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                <p className="text-[12px] text-gray-500 font-medium">No documents {search ? 'match' : 'in this subject yet'}</p>
                {!search && (
                  <Button size="sm" onClick={() => setShowSubmit(true)} className="mt-3 gap-1.5" style={{ background: A }} data-testid="teacher-hub-empty-submit"><Send className="w-3.5 h-3.5 text-white" /><span className="text-white">{autoStatus.enabled ? 'Publish first document' : 'Submit first contribution'}</span></Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {docs.map(d => {
                  const f = fileBadge(d.mime_type, d.file_name);
                  return (
                    <div key={d.id} className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3 hover:shadow-sm hover:border-[#34A853]/40 transition-all" data-testid={`teacher-hub-doc-${d.id}`}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-[10px] font-extrabold" style={{ background: f.bg }}>{f.label}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-[13.5px] font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{d.title}</p>
                          {d.is_teacher_contribution && <span className="text-[9px] font-bold uppercase bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded border border-purple-100">Teacher</span>}
                          {d.auto_approved && <span className="text-[9px] font-bold uppercase bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-100 flex items-center gap-1"><Zap className="w-2.5 h-2.5" /> auto</span>}
                        </div>
                        {d.description && <p className="text-[11px] text-gray-600 line-clamp-1">{d.description}</p>}
                        <p className="text-[10px] text-gray-500">{d.uploader_name} · {fmtBytes(d.file_size)} · {fmtDate(d.published_at || d.created_at)}</p>
                      </div>
                      <button onClick={() => setPreviewDoc(d)} className="text-[11px] font-semibold text-[#0d9488] hover:text-white hover:bg-[#34A853] border border-[#34A853]/30 hover:border-[#34A853] px-2.5 py-1 rounded-md flex items-center gap-1 transition-all" data-testid={`teacher-hub-doc-view-${d.id}`}>
                        <Eye className="w-3 h-3" /> View
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {showSubmit && (
        <TeacherSubmitModal
          A={A}
          dept={dept}
          subject={selectedSubject}
          deptSubjects={subjects}
          folders={folders}
          currentFolderId={currentFolder?.id}
          autoApprovalOn={autoStatus.enabled}
          onClose={() => setShowSubmit(false)}
          onDone={() => { setShowSubmit(false); loadInner(); loadSubjects(); onSubmitted?.(); }}
        />
      )}
      {showNewSubject && (
        <TeacherSubjectModal
          A={A}
          dept={dept}
          onClose={() => setShowNewSubject(false)}
          onDone={() => { setShowNewSubject(false); loadSubjects(); }}
        />
      )}
      {showNewFolder && selectedSubject && (
        <TeacherFolderModal
          A={A}
          dept={dept}
          subject={selectedSubject}
          parentFolder={currentFolder}
          onClose={() => setShowNewFolder(false)}
          onDone={() => { setShowNewFolder(false); loadInner(); }}
        />
      )}
      {previewDoc && (
        <DocViewer doc={previewDoc} departmentId={dept.id} onClose={() => setPreviewDoc(null)} accent={A} />
      )}
    </div>
  );
}

/* ── Teacher subject create ───────────────────────────────────────────── */
function TeacherSubjectModal({ A, dept, onClose, onDone }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    const r = await jsonApi(`/hub/repo/${dept.id}/subjects`, {
      method: 'POST',
      body: JSON.stringify({ name: name.trim(), code: code.trim(), description: description.trim() }),
    });
    setSaving(false);
    if (r?.detail) { setErr(friendlyError(r)); return; }
    toast.success('Subject created');
    onDone();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="teacher-hub-subject-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={onSubmit} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${A}08, transparent)` }}>
          <h3 className="text-sm font-extrabold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}><BookOpen className="w-4 h-4" style={{ color: A }} /> New Subject in {dept.name}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[11px] text-gray-500">Subjects help organize the library by topic. Anyone in this department will see your subject immediately.</p>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Name <span className="text-red-500">*</span></label>
            <input value={name} onChange={e => setName(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-gray-200 px-3 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40" placeholder="e.g. Physics, Algebra, Literature" data-testid="teacher-hub-subject-name" autoFocus />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Subject Code</label>
            <input value={code} onChange={e => setCode(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-gray-200 px-3 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40 tabular-nums" placeholder="PHY101" data-testid="teacher-hub-subject-code" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="mt-1.5 w-full text-sm rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40 resize-none" data-testid="teacher-hub-subject-desc" />
          </div>
          {err && <p className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5">{err}</p>}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" disabled={saving} style={{ background: A }} className="text-white" data-testid="teacher-hub-subject-submit">{saving ? 'Creating...' : 'Create Subject'}</Button>
        </div>
      </form>
    </div>
  );
}

/* ── Teacher folder create — same contribution privilege as subjects, but
   scoped inside a subject (the backend requires subject_id for a teacher's
   folder.create, see hub_routes.py _require_can_create_folder) ──────────── */
function TeacherFolderModal({ A, dept, subject, parentFolder, onClose, onDone }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    const r = await jsonApi(`/hub/repo/${dept.id}/folders`, {
      method: 'POST',
      body: JSON.stringify({ name: name.trim(), description: description.trim(), subject_id: subject.id, parent_id: parentFolder?.id || null }),
    });
    setSaving(false);
    if (r?.detail) { setErr(friendlyError(r)); return; }
    toast.success('Folder created');
    onDone();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="teacher-hub-folder-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={onSubmit} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${A}08, transparent)` }}>
          <h3 className="text-sm font-extrabold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <FolderOpen className="w-4 h-4" style={{ color: A }} /> New Folder in {parentFolder ? parentFolder.name : subject.name}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[11px] text-gray-500">Folders help organize documents within a subject. Anyone in this department will see it immediately.</p>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Name <span className="text-red-500">*</span></label>
            <input value={name} onChange={e => setName(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-gray-200 px-3 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40" placeholder="e.g. Chapter 1, Worksheets, Past Papers" data-testid="teacher-hub-folder-name" autoFocus />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="mt-1.5 w-full text-sm rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40 resize-none" data-testid="teacher-hub-folder-desc" />
          </div>
          {err && <p className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5">{err}</p>}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" disabled={saving} style={{ background: A }} className="text-white" data-testid="teacher-hub-folder-submit">{saving ? 'Creating...' : 'Create Folder'}</Button>
        </div>
      </form>
    </div>
  );
}

function TeacherDocPreview({ A, dept, doc, onClose }) {
  const download = async () => {
    const token = localStorage.getItem('desky_token');
    const res = await fetch(`${API}/api/hub/repo/${dept.id}/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error('Download failed. Please try again.'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = doc.file_name || 'document'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const f = fileBadge(doc.mime_type, doc.file_name);
  const isPdf = f.label === 'PDF';
  const isImage = f.label === 'IMG';
  const canPreviewInline = isPdf || isImage;
  // dl=0 (default) serves Content-Disposition: inline so the browser renders
  // the PDF/image directly instead of downloading it; ?token= is the same
  // query-param auth fallback the backend already offers third-party
  // embedders (see backend/hub_routes.py download_document) — an <iframe>/
  // <img> can't send an Authorization header the way fetch() can.
  const token = localStorage.getItem('desky_token');
  const viewUrl = `${API}/api/hub/repo/${dept.id}/documents/${doc.id}/download?dl=0&token=${encodeURIComponent(token || '')}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="teacher-hub-preview">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full flex flex-col ${canPreviewInline ? 'max-w-4xl h-[88vh]' : 'max-w-lg'}`}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-start gap-3 flex-shrink-0" style={{ background: `linear-gradient(135deg, ${A}06, transparent)` }}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-[10px] font-extrabold" style={{ background: f.bg }}>{f.label}</div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-extrabold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{doc.title}</h3>
            <p className="text-[10px] text-gray-500 mt-0.5">{doc.file_name} · {fmtBytes(doc.file_size)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>

        {canPreviewInline && (
          <div className="flex-1 min-h-0 bg-gray-100 flex items-center justify-center overflow-auto">
            {isPdf ? (
              <iframe src={viewUrl} title={doc.title} className="w-full h-full border-0" data-testid="teacher-hub-preview-frame" />
            ) : (
              <img src={viewUrl} alt={doc.title} className="max-w-full max-h-full object-contain" data-testid="teacher-hub-preview-image" />
            )}
          </div>
        )}

        <div className="p-5 space-y-2 text-[12px] text-gray-700 flex-shrink-0">
          {doc.description && <p>{doc.description}</p>}
          <p className="text-[10.5px] text-gray-500">Uploaded by <span className="font-semibold text-gray-700">{doc.uploader_name}</span> · {fmtDate(doc.published_at || doc.created_at)}</p>
          {(doc.tags || []).length > 0 && <div className="flex items-center gap-1 flex-wrap">{doc.tags.map((t, i) => <span key={i} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{t}</span>)}</div>}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          <Button size="sm" onClick={download} style={{ background: A }} className="text-white gap-1.5" data-testid="teacher-hub-download"><Download className="w-3.5 h-3.5" /> Download</Button>
        </div>
      </div>
    </div>
  );
}

function TeacherSubmitModal({ A, dept, subject, folders, currentFolderId, autoApprovalOn, onClose, onDone, deptSubjects = [] }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [folderId, setFolderId] = useState(currentFolderId || '');
  const [subjectId, setSubjectId] = useState(subject?.id || '');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { setErr('File must be under 25 MB'); return; }
    setErr(''); setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^/.]+$/, ''));
  };
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setErr('Pick a file'); return; }
    if (!title.trim()) { setErr('Title required'); return; }
    setSaving(true); setErr('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title.trim());
    fd.append('description', description.trim());
    if (subjectId) fd.append('subject_id', subjectId);
    if (folderId) fd.append('folder_id', folderId);
    if (tags.trim()) fd.append('tags', tags.trim());
    const r = await formApi(`/hub/submissions/${dept.id}`, fd);
    setSaving(false);
    if (r?.detail) { setErr(friendlyError(r)); return; }
    toast.success(r?.auto_approved ? 'Published to the library' : 'Submitted — awaiting admin review', {
      description: r?.auto_approved ? undefined : 'Track its status under My Submissions.',
    });
    onDone();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="teacher-hub-submit-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={onSubmit} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[88vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${A}08, transparent)` }}>
          <h3 className="text-sm font-extrabold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Send className="w-4 h-4" style={{ color: A }} /> {autoApprovalOn ? 'Publish' : 'Submit'} to {subject ? subject.name : dept.name}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {autoApprovalOn ? (
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2.5 flex items-start gap-2">
              <Zap className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-emerald-800 leading-relaxed">This department has <strong>auto-publish ON</strong>. Your file will appear in the library immediately — no review queue.</p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-amber-800 leading-relaxed">A department admin will approve or request changes before this is published. Track status in <strong>My Submissions</strong>.</p>
            </div>
          )}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">File <span className="text-red-500">*</span></label>
            <div className="mt-1.5 border-2 border-dashed border-gray-200 rounded-xl p-5 text-center cursor-pointer hover:border-[#34A853]/60 transition-colors" onClick={() => inputRef.current?.click()} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }} data-testid="teacher-hub-submit-dropzone">
              <input ref={inputRef} type="file" onChange={e => pick(e.target.files?.[0])} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp" data-testid="teacher-hub-submit-file" />
              {file ? <div><FileText className="w-7 h-7 mx-auto mb-1.5" style={{ color: A }} /><p className="text-[12px] font-bold text-gray-900 truncate">{file.name}</p><p className="text-[10px] text-gray-500 mt-0.5">{fmtBytes(file.size)}</p></div> : <div><Upload className="w-7 h-7 text-gray-300 mx-auto mb-1.5" /><p className="text-[12px] text-gray-600 font-medium">Click or drop a file</p><p className="text-[10px] text-gray-400 mt-0.5">PDF, DOC, images — up to 25 MB</p></div>}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Title <span className="text-red-500">*</span></label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-gray-200 px-3 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40" placeholder="What's this document about?" data-testid="teacher-hub-submit-title" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="mt-1.5 w-full text-sm rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40 resize-none" placeholder="Context for the reviewer" data-testid="teacher-hub-submit-desc" />
          </div>
          {!subject && deptSubjects.length > 0 && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Subject</label>
              <select value={subjectId} onChange={e => setSubjectId(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-gray-200 px-2 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40" data-testid="teacher-hub-submit-subject">
                <option value="">(General — department root)</option>
                {deptSubjects.map(s => <option key={s.id} value={s.id}>{s.name}{s.code ? ` · ${s.code}` : ''}</option>)}
              </select>
              <p className="text-[10px] text-gray-400 mt-1">Categorizes your contribution. Reviewer can change it.</p>
            </div>
          )}
          {folders.length > 0 && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Suggested Folder</label>
              <select value={folderId} onChange={e => setFolderId(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-gray-200 px-2 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40" data-testid="teacher-hub-submit-folder">
                <option value="">(Root)</option>
                {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Tags</label>
            <input value={tags} onChange={e => setTags(e.target.value)} className="mt-1.5 w-full h-9 text-sm rounded-lg border border-gray-200 px-3 focus:outline-none focus:ring-1 focus:ring-[#34A853]/40" placeholder="research, physics, class-11" data-testid="teacher-hub-submit-tags" />
          </div>
          {err && <p className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5">{err}</p>}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" disabled={saving || !file} style={{ background: A }} className="text-white gap-1.5" data-testid="teacher-hub-submit-do">
            {saving ? (autoApprovalOn ? 'Publishing...' : 'Submitting...') : <><Send className="w-3.5 h-3.5" /> {autoApprovalOn ? 'Publish Now' : 'Submit for Review'}</>}
          </Button>
        </div>
      </form>
    </div>
  );
}

function MySubmissionsView({ A, subs, onRefresh }) {
  const filt = useMemo(() => subs, [subs]);
  if (filt.length === 0) {
    return (
      <div className="text-center py-16 border border-dashed border-gray-200 rounded-xl" data-testid="teacher-hub-my-empty">
        <Send className="w-12 h-12 text-gray-200 mx-auto mb-2" />
        <p className="text-[13px] text-gray-500 font-medium">You haven't submitted anything yet</p>
        <p className="text-[11px] text-gray-400 mt-1">Browse a department library and click "Contribute to this Library" to share your PDFs, notes, or research.</p>
      </div>
    );
  }
  const statusMeta = {
    pending: { bg: 'bg-amber-50 text-amber-700 border-amber-100', icon: Clock },
    approved: { bg: 'bg-emerald-50 text-emerald-700 border-emerald-100', icon: CheckCircle },
    rejected: { bg: 'bg-red-50 text-red-700 border-red-100', icon: XCircle },
    changes_requested: { bg: 'bg-blue-50 text-blue-700 border-blue-100', icon: AlertCircle },
  };
  return (
    <div className="space-y-2" data-testid="teacher-hub-my-subs">
      {filt.map(s => {
        const meta = statusMeta[s.status] || statusMeta.pending;
        const Icon = meta.icon;
        const f = fileBadge(s.mime_type, s.file_name);
        return (
          <div key={s.id} className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3 hover:shadow-sm transition-all" data-testid={`teacher-hub-my-sub-${s.id}`}>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-[10px] font-extrabold" style={{ background: f.bg }}>{f.label}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-[13px] font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.title}</p>
                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border flex items-center gap-1 ${meta.bg}`}><Icon className="w-2.5 h-2.5" /> {s.status.replace('_', ' ')}</span>
              </div>
              <p className="text-[10px] text-gray-500">to <span className="font-semibold text-gray-700">{s.target_department_name || 'department'}</span> · {fmtBytes(s.file_size)} · submitted {fmtDate(s.submitted_at)}</p>
              {s.reviewer_notes && <p className="text-[10.5px] italic text-gray-600 mt-0.5">Reviewer: "{s.reviewer_notes}"</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
